import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { Tabs } from "antd";
import type { TabsProps } from "antd";
import "antd/dist/reset.css";

import { changeSelectedProductType } from "../../store/actions";
import { Action } from "../../store/actionTypes";
import { getFormState } from "../../store/selectors";
import { SaveState } from "app/shared/ui/GenericSaveAndBackButton";
import { OfferingType } from "app/shared/model/EditPricelist";

const ACTIVE_TAB_STORAGE_KEY = "pricelistActiveProductType";

const TAB_KEY_TO_PRODUCT_TYPE: Record<string, OfferingType> = {
  Accommodation: "Accommodation",
  Courses: "Course",
  AddOns: "AddOn",
  Transfers: "Transfer",
  Fees: "Fee",
  Packages: "Package",
};

const PRODUCT_TYPE_TO_TAB_KEY: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  Object.entries(TAB_KEY_TO_PRODUCT_TYPE).forEach(([key, value]) => {
    map[value] = key;
  });
  return map;
})();

const readStoredProductType = (): OfferingType | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const value = window.localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    return (value as OfferingType) ?? undefined;
  } catch {
    return undefined;
  }
};

const persistProductType = (productType: OfferingType) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, productType);
  } catch {}
};

export interface PricelistFooterProps {}

interface StateProps {
  formState: SaveState;
}

interface DispatchProps {
  changeSelectedProductType: (value: string) => void;
}

type InternalProps = StateProps & DispatchProps;

const PricelistFooterView = (props: InternalProps) => (
  <PricelistFooter {...props} />
);

function PricelistFooter(props: InternalProps) {
  const storedProductType = React.useMemo(() => readStoredProductType(), []);
  const initialTabKey = storedProductType
    ? PRODUCT_TYPE_TO_TAB_KEY[storedProductType]
    : undefined;

  const [activeTabItem, setActiveTabItem] = React.useState<string | undefined>(
    initialTabKey
  );
  const restoreAttemptedRef = React.useRef(false);

  const handleClick = React.useCallback(
    (tabKey: string, productType: OfferingType) => {
      setActiveTabItem(tabKey);
      props.changeSelectedProductType(productType);
      persistProductType(productType);
    },
    [props.changeSelectedProductType]
  );

  React.useEffect(() => {
    if (restoreAttemptedRef.current) return;
    restoreAttemptedRef.current = true;

    const storedType = storedProductType;
    const storedKey = storedType
      ? PRODUCT_TYPE_TO_TAB_KEY[storedType]
      : undefined;

    if (storedType && storedKey) {
      handleClick(storedKey, storedType);
    }
  }, [storedProductType, handleClick]);

  const isDisabled =
    props.formState === SaveState.PendingChanges ||
    props.formState === SaveState.NoChanges ||
    props.formState === SaveState.ValidationError;

  const items: TabsProps["items"] = [
    { key: "Accommodation", label: "Accommodation", disabled: isDisabled },
    { key: "Courses", label: "Courses", disabled: isDisabled },
    { key: "AddOns", label: "Add Ons", disabled: isDisabled },
    { key: "Transfers", label: "Transfers", disabled: isDisabled },
    { key: "Fees", label: "Fees", disabled: isDisabled },
    { key: "Packages", label: "Packages", disabled: isDisabled },
  ];

  return (
    <Tabs
      type="card"
      items={items}
      activeKey={activeTabItem ?? "Accommodation"}
      onChange={(key) => {
        const productType =
          TAB_KEY_TO_PRODUCT_TYPE[key] ?? ("Accommodation" as OfferingType);
        handleClick(key, productType);
      }}
    />
  );
}

const mapStateToProps = (state: any): StateProps => ({
  formState: getFormState(state),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, Action>
): DispatchProps => ({
  changeSelectedProductType: (productType: string) =>
    dispatch(changeSelectedProductType(productType)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PricelistFooterView);
